import Component from '@glimmer/component';
import { fn } from '@ember/helper';
import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import type { ComponentLike } from '@glint/template';
import { eq, gte } from '../utilities.ts';
import { Chip } from './buttons.gts';
import { Text } from './typography.gts';

// ============================================================================
// PROGRESS CIRCLE
// ============================================================================

interface ProgressCircleSignature {
  Args: {
    /** Progress value 0-100 */
    progress: number;
    /** Size of the circle in pixels */
    size?: 'sm' | 'md' | 'lg' | number;
    /** Stroke width */
    strokeWidth?: number;
    /** Progress bar color (tailwind color or hex) */
    progressColor?: string;
    /** Background track color */
    trackColor?: string;
    /** Show percentage label in center */
    showLabel?: boolean;
    /** Custom label (overrides percentage) */
    label?: string;
    /** Secondary text below label */
    sublabel?: string;
  };
  Element: HTMLDivElement;
}

export class ProgressCircle extends Component<ProgressCircleSignature> {
  get sizeValue() {
    const sizeMap = { sm: 64, md: 96, lg: 128 };
    if (typeof this.args.size === 'number') return this.args.size;
    return sizeMap[this.args.size ?? 'md'];
  }

  get strokeWidth() {
    return this.args.strokeWidth ?? 8;
  }

  get radius() {
    return (this.sizeValue - this.strokeWidth) / 2;
  }

  get circumference() {
    return 2 * Math.PI * this.radius;
  }

  get strokeDashoffset() {
    const progress = Math.max(0, Math.min(100, this.args.progress));
    return this.circumference - (progress / 100) * this.circumference;
  }

  get progressStyle() {
    return htmlSafe(
      `stroke-dasharray: ${this.circumference}; stroke-dashoffset: ${this.strokeDashoffset}`
    );
  }

  get containerStyle() {
    return htmlSafe(`width: ${this.sizeValue}px; height: ${this.sizeValue}px`);
  }

  get progressColor() {
    return this.args.progressColor ?? '#6366f1';
  }

  get trackColor() {
    return this.args.trackColor ?? '#e5e7eb';
  }

  get showLabel() {
    return this.args.showLabel !== false;
  }

  get displayLabel() {
    return this.args.label ?? `${Math.round(this.args.progress)}%`;
  }

  get labelSize() {
    if (this.sizeValue <= 64) return 'text-sm';
    if (this.sizeValue <= 96) return 'text-lg';
    return 'text-xl';
  }

  <template>
    <div class="relative flex-shrink-0" style={{this.containerStyle}} ...attributes>
      <svg
        class="w-full h-full transform -rotate-90"
        viewBox="0 0 {{this.sizeValue}} {{this.sizeValue}}"
      >
        {{! Background track }}
        <circle
          cx={{this.sizeValue}}
          cy={{this.sizeValue}}
          r={{this.radius}}
          fill="none"
          stroke={{this.trackColor}}
          stroke-width={{this.strokeWidth}}
          transform="translate({{this.sizeValue}} {{this.sizeValue}}) scale(0.5)"
        />
        {{! Progress arc }}
        <circle
          cx={{this.sizeValue}}
          cy={{this.sizeValue}}
          r={{this.radius}}
          fill="none"
          stroke={{this.progressColor}}
          stroke-width={{this.strokeWidth}}
          stroke-linecap="round"
          style={{this.progressStyle}}
          transform="translate({{this.sizeValue}} {{this.sizeValue}}) scale(0.5)"
        />
      </svg>
      {{#if this.showLabel}}
        <div class="absolute inset-0 flex flex-col items-center justify-center">
          <span class="font-bold text-gray-900 {{this.labelSize}}">{{this.displayLabel}}</span>
          {{#if @sublabel}}
            <span class="text-xs text-gray-500">{{@sublabel}}</span>
          {{/if}}
        </div>
      {{/if}}
    </div>
  </template>
}

// ============================================================================
// ICON CIRCLE
// ============================================================================

interface IconCircleSignature {
  Args: {
    /** Size of the circle */
    size?: 'sm' | 'md' | 'lg';
    /** Intent/color scheme */
    intent?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
    /** Custom background class (overrides intent) */
    bgClass?: string;
    /** Custom text/icon color class */
    colorClass?: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export class IconCircle extends Component<IconCircleSignature> {
  get sizeClass() {
    switch (this.args.size) {
      case 'sm':
        return 'w-8 h-8';
      case 'lg':
        return 'w-16 h-16';
      default:
        return 'w-12 h-12';
    }
  }

  get intentClasses() {
    if (this.args.bgClass) return this.args.bgClass;
    switch (this.args.intent) {
      case 'primary':
        return 'bg-violet-100 text-violet-600';
      case 'success':
        return 'bg-green-100 text-green-600';
      case 'warning':
        return 'bg-amber-100 text-amber-600';
      case 'danger':
        return 'bg-red-100 text-red-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  }

  <template>
    <div
      class="rounded-full flex items-center justify-center flex-shrink-0
        {{this.sizeClass}}
        {{this.intentClasses}}
        {{@colorClass}}"
      ...attributes
    >
      {{yield}}
    </div>
  </template>
}

export interface FilterTab {
  id: string;
  label: string;
  count?: number;
  icon?: ComponentLike<{ Element: HTMLElement; Args: { class?: string } }>;
}

interface FilterTabsSignature {
  Args: {
    tabs: FilterTab[];
    activeId?: string;
    onSelect?: (id: string) => void;
    activeClass?: string;
    inactiveClass?: string;
    countActiveClass?: string;
    countInactiveClass?: string;
    showCounts?: boolean;
  };
  Element: HTMLDivElement;
}

export class FilterTabs extends Component<FilterTabsSignature> {
  get activeClass() {
    return this.args.activeClass ?? 'bg-violet-600 text-white shadow-md';
  }

  get inactiveClass() {
    return (
      this.args.inactiveClass ?? 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
    );
  }

  get countActiveClass() {
    return this.args.countActiveClass ?? 'bg-violet-500';
  }

  get countInactiveClass() {
    return this.args.countInactiveClass ?? 'bg-gray-200';
  }

  get showCounts() {
    return this.args.showCounts !== false;
  }

  handleSelect = (id: string) => {
    this.args.onSelect?.(id);
  };

  <template>
    <div class="flex flex-wrap gap-2" ...attributes>
      {{#each @tabs as |tab|}}
        <button
          type="button"
          class="px-4 py-2 rounded-full text-sm font-medium transition-all
            {{if (eq @activeId tab.id) this.activeClass this.inactiveClass}}"
          {{on "click" (fn this.handleSelect tab.id)}}
        >
          {{#if tab.icon}}
            {{#let tab.icon as |Icon|}}
              <Icon class="mr-2 inline-flex" />
            {{/let}}
          {{/if}}
          {{tab.label}}
          {{#if this.showCounts}}
            {{#if tab.count}}
              <span
                class="ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                  {{if (eq @activeId tab.id) this.countActiveClass this.countInactiveClass}}"
              >
                {{tab.count}}
              </span>
            {{/if}}
          {{/if}}
        </button>
      {{/each}}
    </div>
  </template>
}

interface MatchScoreBadgeSignature {
  Args: {
    score: number | undefined;
    label?: string;
    size?: 'sm' | 'md' | 'lg';
    getClass?: (score: number) => string;
  };
  Element: HTMLDivElement;
}

export class MatchScoreBadge extends Component<MatchScoreBadgeSignature> {
  // Use odd pixel widths (w-[39px], w-[55px], w-[79px]) for proper centering on even-width borders/rings
  // See typescript.instructions.md "Centering Elements" for explanation
  get score() {
    return this.args.score ?? 0;
  }

  get sizeClass() {
    switch (this.args.size) {
      case 'sm':
        return 'w-[39px] h-[39px] text-xs';
      case 'lg':
        return 'w-[79px] h-[79px] text-xl';
      default:
        return 'w-[55px] h-[55px] text-sm';
    }
  }

  get scoreClass() {
    if (this.args.getClass) {
      return this.args.getClass(this.score);
    }
    if (this.score >= 90) return 'bg-green-100 text-green-700 ring-2 ring-green-300';
    if (this.score >= 80) return 'bg-green-100 text-green-700';
    if (this.score >= 70) return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  }

  <template>
    <div class="text-center" ...attributes>
      <div
        class="rounded-full flex items-center justify-center font-bold
          {{this.sizeClass}}
          {{this.scoreClass}}"
      >
        {{this.score}}%
      </div>
      {{#if @label}}
        <Text @intent="muted" @size="xs" class="mt-1">{{@label}}</Text>
      {{/if}}
    </div>
  </template>
}

interface StatCardSignature {
  Args: {
    icon?: ComponentLike<{ Element: HTMLElement; Args: { size?: number; class?: string } }>;
    value: string;
    label: string;
    trend?: 'up' | 'down' | 'stable';
    trendText?: string;
    class?: string;
    valueClass?: string;
    labelClass?: string;
    trendClass?: string;
    iconClass?: string;
  };
  Element: HTMLDivElement;
}

export class StatCard extends Component<StatCardSignature> {
  get trendClass() {
    if (this.args.trendClass) return this.args.trendClass;
    if (this.args.trend === 'down') return 'text-red-600';
    if (this.args.trend === 'stable') return 'text-gray-500';
    return 'text-green-600';
  }

  <template>
    <div class="bg-white rounded-lg p-4 text-center shadow-sm {{@class}}" ...attributes>
      {{#if @icon}}
        {{#let @icon as |Icon|}}
          <Icon @size={{24}} class="mx-auto mb-2 {{@iconClass}}" />
        {{/let}}
      {{/if}}
      <div class="text-2xl font-bold text-gray-900 {{@valueClass}}">{{@value}}</div>
      <Text @intent="muted" @size="sm" class="{{@labelClass}}">{{@label}}</Text>
      {{#if @trendText}}
        <span class="text-xs font-medium {{this.trendClass}}">{{@trendText}}</span>
      {{/if}}
    </div>
  </template>
}

interface SkillChipSignature {
  Args: {
    name: string;
    level?: number;
    showLevel?: boolean;
    intent?: 'default' | 'primary' | 'success' | 'warning';
  };
  Element: HTMLDivElement;
}

export class SkillChip extends Component<SkillChipSignature> {
  get showLevel() {
    return this.args.showLevel !== false && this.args.level !== undefined;
  }

  get intentClass() {
    switch (this.args.intent) {
      case 'primary':
        return 'bg-violet-100 text-violet-700';
      case 'success':
        return 'bg-green-100 text-green-700';
      case 'warning':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  }

  get barClass() {
    if (this.args.level === undefined) return 'bg-gray-300';
    if (this.args.level >= 80) return 'bg-green-500';
    if (this.args.level >= 60) return 'bg-violet-500';
    return 'bg-amber-500';
  }

  get levelStyle() {
    if (this.args.level === undefined) return htmlSafe('width: 0%');
    return htmlSafe(`width: ${this.args.level}%`);
  }

  <template>
    <div
      class="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 shadow-sm
        {{this.intentClass}}"
      ...attributes
    >
      <span class="font-medium">{{@name}}</span>
      {{#if this.showLevel}}
        <div class="w-16 h-2 bg-white/60 rounded-full overflow-hidden">
          <div class="h-full rounded-full {{this.barClass}}" style={{this.levelStyle}}></div>
        </div>
        <span class="text-sm">{{@level}}%</span>
      {{/if}}
    </div>
  </template>
}

interface StatusBadgeSignature {
  Args: {
    status?: string;
    label?: string;
    intent?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  };
  Element: HTMLSpanElement;
}

export class StatusBadge extends Component<StatusBadgeSignature> {
  get intent() {
    if (this.args.intent) return this.args.intent;
    if (this.args.status === 'completed') return 'success';
    if (this.args.status === 'in-progress') return 'primary';
    if (this.args.status === 'planned') return 'default';
    return 'default';
  }

  get label() {
    return this.args.label ?? this.args.status ?? '';
  }

  <template>
    <Chip @intent={{this.intent}} ...attributes>{{this.label}}</Chip>
  </template>
}

interface TimelineSignature<T = unknown> {
  Args: {
    items: T[];
    lineClass?: string;
    listClass?: string;
    itemClass?: string;
    nodeClass?: string;
    contentClass?: string;
  };
  Blocks: {
    node?: [item: T, index: number];
    content?: [item: T, index: number];
    default?: [item: T, index: number];
  };
  Element: HTMLDivElement;
}

export class Timeline<T = unknown> extends Component<TimelineSignature<T>> {
  get lineClass() {
    return this.args.lineClass ?? 'bg-violet-200';
  }

  get listClass() {
    return this.args.listClass ?? 'space-y-4';
  }

  get itemClass() {
    return this.args.itemClass ?? 'relative flex items-start gap-4';
  }

  get nodeClass() {
    return this.args.nodeClass ?? 'absolute -left-8';
  }

  get contentClass() {
    return this.args.contentClass ?? 'flex-1';
  }

  <template>
    <div class="relative pl-12" ...attributes>
      <div class="absolute left-[18px] top-0 bottom-0 w-0.5 {{this.lineClass}}"></div>
      <div class="{{this.listClass}}">
        {{#each @items as |item index|}}
          <div class="{{this.itemClass}}">
            {{#if (has-block "node")}}
              <div class="{{this.nodeClass}}">
                {{yield item index to="node"}}
              </div>
            {{/if}}
            <div class="{{this.contentClass}}">
              {{#if (has-block "content")}}
                {{yield item index to="content"}}
              {{else}}
                {{yield item index}}
              {{/if}}
            </div>
          </div>
        {{/each}}
      </div>
    </div>
  </template>
}

interface TaEligibilityBadgeSignature {
  Args: {
    eligible?: boolean;
    amount?: string;
    partial?: boolean;
    partialAmount?: string;
    totalAmount?: string;
  };
  Element: HTMLDivElement;
}

export class TaEligibilityBadge extends Component<TaEligibilityBadgeSignature> {
  get isEligible() {
    return this.args.eligible !== false;
  }

  get isPartial() {
    return this.args.partial === true;
  }

  <template>
    {{#if this.isEligible}}
      {{#if this.isPartial}}
        {{! Partial coverage }}
        <div
          class="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 text-amber-700 rounded-lg border border-amber-200"
          ...attributes
        >
          <svg
            class="w-[18px] h-[18px]"
            fill="currentColor"
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M215.79,118.17a8,8,0,0,0-5-5.66L153.18,90.9l14.66-73.33a8,8,0,0,0-13.69-7l-112,120a8,8,0,0,0,3,13l57.63,21.61L88.16,238.43a8,8,0,0,0,13.69,7l112-120A8,8,0,0,0,215.79,118.17Z"
            ></path>
          </svg>
          <span class="font-medium">Partial Coverage</span>
          {{#if @partialAmount}}
            <span class="text-amber-600">
              {{@partialAmount}}
              {{#if @totalAmount}}
                of
                {{@totalAmount}}
              {{/if}}
            </span>
          {{/if}}
        </div>
      {{else}}
        {{! Fully covered }}
        <div
          class="inline-flex items-center gap-2 px-3 py-2 bg-green-50 text-green-700 rounded-lg border border-green-200"
          ...attributes
        >
          <svg
            class="w-[18px] h-[18px]"
            fill="currentColor"
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M173.66,98.34a8,8,0,0,1,0,11.32l-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35A8,8,0,0,1,173.66,98.34ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"
            ></path>
          </svg>
          <span class="font-medium">TA Eligible</span>
          {{#if @amount}}
            <span class="text-green-600">{{@amount}} covered</span>
          {{/if}}
        </div>
      {{/if}}
    {{else}}
      {{! Not eligible }}
      <div
        class="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-500 rounded-lg border border-gray-200"
        ...attributes
      >
        <svg
          class="w-[18px] h-[18px]"
          fill="currentColor"
          viewBox="0 0 256 256"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M165.66,101.66,139.31,128l26.35,26.34a8,8,0,0,1-11.32,11.32L128,139.31l-26.34,26.35a8,8,0,0,1-11.32-11.32L116.69,128,90.34,101.66a8,8,0,0,1,11.32-11.32L128,116.69l26.34-26.35a8,8,0,0,1,11.32,11.32ZM232,128A104,104,0,1,1,128,24,104.11,104.11,0,0,1,232,128Zm-16,0a88,88,0,1,0-88,88A88.1,88.1,0,0,0,216,128Z"
          ></path>
        </svg>
        <span>Not TA Eligible</span>
      </div>
    {{/if}}
  </template>
}

interface ActionableCardSignature {
  Args: {
    title?: string;
    matchScore?: number;
    showBanner?: boolean;
    bannerText?: string;
    bannerClass?: string;
    cardClass?: string;
  };
  Blocks: {
    banner?: [];
    badge?: [];
    header?: [];
    metadata?: [];
    skills?: [];
    eligibility?: [];
    actions?: [];
    default?: [];
  };
  Element: HTMLDivElement;
}

export class ActionableCard extends Component<ActionableCardSignature> {
  get bannerClass() {
    return (
      this.args.bannerClass ??
      'bg-gradient-to-r from-violet-500 to-purple-500 text-white text-sm font-medium px-4 py-2 flex items-center gap-2'
    );
  }

  get cardClass() {
    return (
      this.args.cardClass ?? 'bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'
    );
  }

  get showDefaultBanner() {
    return this.args.showBanner === true && !this.args.bannerText;
  }

  <template>
    <div class={{this.cardClass}} ...attributes>
      {{#if (has-block "banner")}}
        {{yield to="banner"}}
      {{else if @showBanner}}
        <div class={{this.bannerClass}}>
          <svg
            class="w-4 h-4"
            fill="currentColor"
            viewBox="0 0 256 256"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M234.5,114.38l-45.1,39.36,13.51,58.6a16,16,0,0,1-23.84,17.34l-51.11-31-51,31a16,16,0,0,1-23.84-17.34L66.61,153.8,21.5,114.38a16,16,0,0,1,9.11-28.06l59.46-5.15,23.21-55.36a15.95,15.95,0,0,1,29.44,0h0L166,81.17l59.44,5.15a16,16,0,0,1,9.11,28.06Z"
            ></path>
          </svg>
          {{@bannerText}}
        </div>
      {{/if}}

      <div class="p-4">
        {{#if (has-block "header")}}
          {{yield to="header"}}
        {{else if @title}}
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <h4 class="font-semibold text-lg">{{@title}}</h4>
                {{#if (has-block "badge")}}
                  {{yield to="badge"}}
                {{/if}}
              </div>
              {{#if (has-block "metadata")}}
                {{yield to="metadata"}}
              {{/if}}
            </div>
            {{#if @matchScore}}
              <div class="text-center">
                <div
                  class="w-14 h-14 rounded-full flex items-center justify-center font-bold ring-2
                    {{if
                      (gte @matchScore 90)
                      'bg-green-100 text-green-700 ring-green-200'
                      (if
                        (gte @matchScore 70)
                        'bg-amber-100 text-amber-700 ring-amber-200'
                        'bg-gray-100 text-gray-700 ring-gray-200'
                      )
                    }}"
                >
                  {{@matchScore}}%
                </div>
                <span class="text-xs text-gray-500 mt-1">Match</span>
              </div>
            {{/if}}
          </div>
        {{/if}}

        {{#if (has-block "skills")}}
          <div class="flex flex-wrap gap-2 mb-4">
            {{yield to="skills"}}
          </div>
        {{/if}}

        {{#if (has-block "eligibility")}}
          <div class="mb-4">
            {{yield to="eligibility"}}
          </div>
        {{/if}}

        {{#if (has-block "actions")}}
          <div class="flex items-center justify-between pt-4 border-t border-gray-100">
            {{yield to="actions"}}
          </div>
        {{/if}}

        {{#if (has-block)}}
          {{yield}}
        {{/if}}
      </div>
    </div>
  </template>
}

// ============================================================================
// PAGE HERO
// ============================================================================

interface PageHeroSignature {
  Args: {
    /** Gradient theme preset */
    theme?: 'violet' | 'emerald' | 'amber' | 'teal' | 'gray';
    /** Custom gradient class (overrides theme) */
    gradientClass?: string;
    /** Rounded corners */
    rounded?: 'lg' | 'xl' | '2xl';
  };
  Blocks: {
    default: [];
    icon?: [];
    badge?: [];
  };
  Element: HTMLDivElement;
}

export class PageHero extends Component<PageHeroSignature> {
  get gradientClass() {
    if (this.args.gradientClass) return this.args.gradientClass;
    switch (this.args.theme) {
      case 'emerald':
        return 'bg-gradient-to-r from-emerald-600 to-teal-600';
      case 'amber':
        return 'bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500';
      case 'teal':
        return 'bg-gradient-to-br from-teal-600 via-teal-700 to-cyan-800';
      case 'gray':
        return 'bg-gradient-to-r from-gray-800 to-gray-900';
      default:
        return 'bg-gradient-to-r from-violet-600 to-purple-600';
    }
  }

  get roundedClass() {
    switch (this.args.rounded) {
      case 'lg':
        return 'rounded-lg';
      case '2xl':
        return 'rounded-2xl';
      default:
        return 'rounded-xl';
    }
  }

  <template>
    <div
      class="p-6 md:p-8 text-white mb-6 relative overflow-hidden
        {{this.gradientClass}}
        {{this.roundedClass}}"
      ...attributes
    >
      {{#if (has-block "icon")}}
        <div class="flex items-center gap-3 mb-3">
          {{yield to="icon"}}
        </div>
      {{/if}}
      {{yield}}
      {{#if (has-block "badge")}}
        <div class="flex flex-wrap gap-2 mt-4">
          {{yield to="badge"}}
        </div>
      {{/if}}
    </div>
  </template>
}
